import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { MapPin, X, Plus, Loader2, Check, Euro } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useEffect } from "react";

const restaurantIcon = L.divIcon({
  className: 'restaurant-marker',
  html: '<div style="background-color: #f97316; width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 16px; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">R</div>',
  iconSize: [44, 44], iconAnchor: [22, 22]
});

const ZONE_COLORS = ["#22c55e", "#eab308", "#f97316", "#ef4444", "#8b5cf6", "#06b6d4"];

function MapUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom || 13);
  }, [center, zoom, map]);
  return null;
}

export const EnviosTab = ({ authAxios, restaurant, onUpdate }) => {
  const [zones, setZones] = useState(restaurant?.delivery_zones || []);
  const [defaultFee, setDefaultFee] = useState(restaurant?.default_shipping_fee ?? 2.50);
  const [deliveryFreeKm, setDeliveryFreeKm] = useState(restaurant?.delivery_free_km ?? 3.0);
  const [minOrderAmount, setMinOrderAmount] = useState(restaurant?.min_order_amount ?? 0);
  const [restLat, setRestLat] = useState(restaurant?.lat || "");
  const [restLng, setRestLng] = useState(restaurant?.lng || "");
  const [saving, setSaving] = useState(false);

  const addZone = () => {
    const lastKm = zones.length > 0 ? zones[zones.length - 1].max_km + 5 : 5;
    setZones([...zones, { max_km: lastKm, fee: 0, free_above: null, active: true }]);
  };
  const updateZone = (idx, field, value) => setZones(zones.map((z, i) => i === idx ? { ...z, [field]: value } : z));
  const removeZone = (idx) => setZones(zones.filter((_, i) => i !== idx));

  const saveZones = async () => {
    setSaving(true);
    try {
      const activeZones = zones.filter(z => z.active !== false).sort((a, b) => a.max_km - b.max_km);
      await authAxios.patch("/restaurant", {
        delivery_zones: activeZones, default_shipping_fee: isNaN(parseFloat(defaultFee)) ? 0 : parseFloat(defaultFee),
        delivery_free_km: isNaN(parseFloat(deliveryFreeKm)) ? 0 : parseFloat(deliveryFreeKm),
        min_order_amount: parseFloat(minOrderAmount) || 0,
        lat: parseFloat(restLat) || null, lng: parseFloat(restLng) || null,
      });
      toast.success("Zonas de envio actualizadas");
      onUpdate();
    } catch { toast.error("Error guardando"); }
    setSaving(false);
  };

  const lat = parseFloat(restLat) || 37.8882;
  const lng = parseFloat(restLng) || -4.7794;
  const activeZones = zones.filter(z => z.active !== false).sort((a, b) => a.max_km - b.max_km);
  const maxRadius = activeZones.length > 0 ? activeZones[activeZones.length - 1].max_km : 0;
  const mapZoom = maxRadius > 30 ? 10 : maxRadius > 15 ? 11 : maxRadius > 8 ? 12 : 13;

  return (
    <>
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="uppercase">Ubicacion del Restaurante</CardTitle>
          <CardDescription>Coordenadas para calcular la distancia de envio y generar links de navegacion.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Latitud</Label><Input type="number" step="0.000001" value={restLat} onChange={e => setRestLat(e.target.value)} placeholder="37.8882" className="bg-secondary border-transparent" data-testid="restaurant-lat" /></div>
            <div className="space-y-2"><Label>Longitud</Label><Input type="number" step="0.000001" value={restLng} onChange={e => setRestLng(e.target.value)} placeholder="-4.7794" className="bg-secondary border-transparent" data-testid="restaurant-lng" /></div>
          </div>
          <p className="text-xs text-muted-foreground">Busca tu restaurante en Google Maps, haz clic derecho y copia las coordenadas.</p>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="uppercase flex items-center justify-between">
            Tarifas de Envio por Distancia
            <Button variant="outline" size="sm" className="gap-1" onClick={addZone} data-testid="add-zone-btn"><Plus className="w-4 h-4" /> Agregar Zona</Button>
          </CardTitle>
          <CardDescription>Configure las tarifas de envio segun la distancia del restaurante al cliente.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 mb-4">
            <Label>Tarifa por defecto (quando nao ha zonas ou coordenadas)</Label>
            <div className="flex items-center gap-2">
              <Euro className="w-4 h-4 text-muted-foreground" />
              <Input type="number" step="0.10" value={defaultFee} onChange={e => setDefaultFee(e.target.value)} className="w-32 bg-secondary border-transparent" data-testid="default-shipping-fee" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <Label>Entrega gratis ate (km)</Label>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <Input type="number" step="0.5" min="0" value={deliveryFreeKm} onChange={e => setDeliveryFreeKm(e.target.value)} className="w-32 bg-secondary border-transparent" data-testid="delivery-free-km" />
                <span className="text-sm text-muted-foreground">km</span>
              </div>
              <p className="text-[11px] text-muted-foreground">Exibido no menu publico como "Entrega gratis ate X km"</p>
            </div>
            <div className="space-y-2">
              <Label>Pedido minimo (EUR)</Label>
              <div className="flex items-center gap-2">
                <Euro className="w-4 h-4 text-muted-foreground" />
                <Input type="number" step="0.50" min="0" value={minOrderAmount} onChange={e => setMinOrderAmount(e.target.value)} className="w-32 bg-secondary border-transparent" data-testid="min-order-amount" />
              </div>
              <p className="text-[11px] text-muted-foreground">Valor minimo para aceitar pedidos de entrega. 0 = sem minimo.</p>
            </div>
          </div>

          {zones.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No hay zonas configuradas. Se usara la tarifa por defecto.</p>
            </div>
          )}

          {zones.map((zone, idx) => (
            <div key={idx} className="flex items-center gap-4 p-3 bg-secondary/50 rounded-lg" data-testid={`zone-row-${idx}`}>
              <Switch checked={zone.active !== false} onCheckedChange={v => updateZone(idx, "active", v)} />
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: ZONE_COLORS[idx % ZONE_COLORS.length] }} />
              <div className="grid grid-cols-3 gap-4 flex-1">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Hasta (km)</Label>
                  <div className="flex items-center gap-1"><Input type="number" step="0.5" value={zone.max_km} onChange={e => updateZone(idx, "max_km", parseFloat(e.target.value) || 0)} className="bg-secondary border-transparent" data-testid={`zone-km-${idx}`} /><span className="text-sm text-muted-foreground">km</span></div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Precio</Label>
                  <div className="flex items-center gap-1"><Euro className="w-3 h-3 text-muted-foreground" /><Input type="number" step="0.10" value={zone.fee} onChange={e => updateZone(idx, "fee", parseFloat(e.target.value) || 0)} className="bg-secondary border-transparent" data-testid={`zone-fee-${idx}`} /></div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Gratis desde (€)</Label>
                  <Input type="number" step="0.01" min="0" value={zone.free_above ?? ""} onChange={e => updateZone(idx, "free_above", e.target.value === "" ? null : Number(e.target.value))} placeholder="Sin límite" className="bg-secondary border-transparent" data-testid={`zone-free-above-${idx}`} />
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeZone(idx)} data-testid={`zone-remove-${idx}`}><X className="w-4 h-4 text-muted-foreground" /></Button>
            </div>
          ))}

          {zones.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700">
              <p className="font-medium mb-1">Resumen de zonas:</p>
              {activeZones.map((z, i, arr) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ZONE_COLORS[zones.indexOf(z) % ZONE_COLORS.length] }} />
                  <p>{i === 0 ? "0" : arr[i-1].max_km} - {z.max_km} km = {z.fee.toFixed(2)} EUR{z.free_above != null ? ` · gratis desde ${Number(z.free_above).toFixed(2)} EUR` : ""}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delivery Radius Map */}
      {restLat && restLng && (
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="uppercase flex items-center gap-2">
              <MapPin className="w-5 h-5 text-emerald-600" /> Mapa de Raio de Entrega
            </CardTitle>
            <CardDescription>
              {activeZones.length > 0
                ? `Visualizacao das ${activeZones.length} zona(s) de entrega configuradas. As areas coloridas mostram o alcance de cada zona.`
                : "Configure zonas de envio acima para ver o raio de entrega no mapa."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[400px] rounded-xl overflow-hidden border-2 border-border" data-testid="delivery-radius-map">
              <MapContainer center={[lat, lng]} zoom={mapZoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
                <MapUpdater center={[lat, lng]} zoom={mapZoom} />
                <Marker position={[lat, lng]} icon={restaurantIcon}>
                  <Popup><strong>{restaurant?.name || "Restaurante"}</strong><br/>Tu ubicacion</Popup>
                </Marker>
                {/* Render circles from largest to smallest so smaller ones are on top */}
                {[...activeZones].reverse().map((zone, revIdx) => {
                  const origIdx = zones.indexOf(zone);
                  const color = ZONE_COLORS[origIdx % ZONE_COLORS.length];
                  return (
                    <Circle
                      key={`zone-circle-${origIdx}`}
                      center={[lat, lng]}
                      radius={zone.max_km * 1000}
                      pathOptions={{
                        color: color,
                        fillColor: color,
                        fillOpacity: 0.08 + (revIdx * 0.04),
                        weight: 2,
                        dashArray: revIdx === 0 ? "8 4" : undefined
                      }}
                    >
                      <Popup>
                        <strong>Zona {origIdx + 1}</strong><br/>
                        Hasta {zone.max_km} km<br/>
                        Tarifa: {zone.fee.toFixed(2)} EUR
                      </Popup>
                    </Circle>
                  );
                })}
              </MapContainer>
            </div>
            {activeZones.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-3">
                {activeZones.map((zone, i) => {
                  const origIdx = zones.indexOf(zone);
                  return (
                    <div key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <div className="w-3 h-3 rounded-full border" style={{ backgroundColor: ZONE_COLORS[origIdx % ZONE_COLORS.length] + "40", borderColor: ZONE_COLORS[origIdx % ZONE_COLORS.length] }} />
                      <span>{zone.max_km} km - {zone.fee.toFixed(2)} EUR</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Button className="glow-orange" onClick={saveZones} disabled={saving} data-testid="save-zones-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}Guardar Configuracion de Envios
      </Button>
    </>
  );
};
